
import Vue from 'vue'
import { categorizeProduct } from '@/utils/CategorizationUtils'
import { PRODUCT_TYPE_ENUM, normalizeProductType } from '@/utils/ProductTypeColors'
import ParserV2Service from '@/services/ParserV2Service'
import { getExcelValue } from '@/utils/parserConstants'

const getExcelProductName = (excelRow, columnMapping) => getExcelValue(excelRow, 'NOM', columnMapping)

const getExcelEAN = (excelRow, columnMapping) => getExcelValue(excelRow, 'EAN', columnMapping)

/**
 * Type de produit à appliquer à une ligne.
 *
 * Le type déclaré du fichier prime sur la détection automatique : un fichier
 * libre-service ne contient que des produits emballés, dont l'EAN à 13 chiffres
 * ferait basculer chaque ligne en « Non-maison » si on laissait categorizeProduct
 * décider. La détection ne sert que sur un fichier sans type déclaré.
 */
const resolveProductType = (state, excelEAN, excelName) =>
  state.fileProductType || categorizeProduct(excelEAN, excelName)

const shouldApplyCategorization = (result) => {
  if (!result?.metadata) return false
  if (result.isManuallyCorrected) return false
  
  const existingType = result.metadata.productType
  return !existingType || 
         existingType === PRODUCT_TYPE_ENUM.TRADITIONNEL || 
         existingType === 'Produit traditionnel'
}

export default {
  namespaced: true,
  state: {
    jsonInput: [],
    jsonResult: {
      originalText: '',
      results: []
    },
    excelHeaders: [],
    currentDraft: null,
    deletedIndexes: [],
    idParsing: null,
    idXlsx: null,
    integrationSuccess: null,
    columnMapping: [],
    // Index de la ligne où l'utilisateur s'est arrêté, pour reprendre au même endroit
    // le lendemain. Un seul repère par brouillon, posé et retiré à la main.
    bookmarkIndex: null,
    rowStatuses: {},
    batchJobId: null,
    batchProgress: {
      processedCount: 0,
      totalCount: 0,
      status: 'pending' // 'pending' | 'processing' | 'completed' | 'failed'
    },
    availableJobs: [],
    selectedJob: null,
    // Type de produit deduit du type du fichier analyse (recettes, libre-service).
    // Sert de repli quand la detection par EAN et mots-cles ne reconnait rien.
    fileProductType: null
  },
  mutations: {
    setFileProductType(state, productType) {
      state.fileProductType = productType || null
    },
    setColumnMapping(state, mapping) {
      state.columnMapping = mapping || [];
    },
    setJsonInput(state, jsonInput) {
      // Freeze to avoid Vue reactivity (critical for performance with 500+ rows)
      // Use Object.freeze instead of deepFreeze to allow getter to work
      const frozenInput = Object.freeze(jsonInput || []);
      state.jsonInput = frozenInput;
      state.deletedIndexes = [];
    },
    setJsonResult(state, jsonResult) {
      state.jsonResult = jsonResult || { originalText: '', results: [] }
      state.deletedIndexes = []
    },
    setExcelHeaders(state, headers) {
      state.excelHeaders = headers || []
    },
    setCurrentDraft(state, draftInfo) {
      state.currentDraft = draftInfo
    },
    clearCurrentDraft(state) {
      state.currentDraft = null
    },
    setParsingMetadata(state, { idParsing, idXlsx, integrationSuccess }) {
      state.idParsing = idParsing
      state.idXlsx = idXlsx
      state.integrationSuccess = integrationSuccess
    },
    deleteLine(state, index) {
      if (index >= 0 && index < state.jsonInput.length && !state.deletedIndexes.includes(index)) {
        // Utiliser Vue.set pour garantir la réactivité
        const newDeletedIndexes = [...state.deletedIndexes, index];
        Vue.set(state, 'deletedIndexes', newDeletedIndexes);
      }
    },
    restoreLine(state, index) {
      if (!state.deletedIndexes.includes(index)) return
      Vue.set(state, 'deletedIndexes', state.deletedIndexes.filter(deleted => deleted !== index))
    },
    resetDeletedIndexes(state) {
      state.deletedIndexes = []
    },
    setAsResult(state, { index, resultIndex }) {
      if (state.jsonResult.results[index]?.[resultIndex]) {
        const results = [...state.jsonResult.results[index]]
        const chosenResult = { ...results[resultIndex], isManuallyCorrected: true }
        const newResults = [chosenResult, ...results.filter((_, i) => i !== resultIndex)]
        // Forcer la réactivité Vue
        Vue.set(state.jsonResult.results, index, newResults)
      } else {
        console.warn('[store setAsResult] ❌ Aucun résultat trouvé à l\'index:', { index, resultIndex, available: state.jsonResult.results[index] });
      }
    },
    setParserPredictions(state, { index, predictions }) {
      if (!state.jsonResult.results) {
        state.jsonResult.results = []
      }
      while (state.jsonResult.results.length <= index) {
        state.jsonResult.results.push([])
      }
      state.jsonResult.results[index] = predictions || []
    },
    setBookmarkIndex(state, index) {
      // Reposer le repère sur la même ligne l'enlève : le bouton fait office de bascule
      state.bookmarkIndex = (state.bookmarkIndex === index) ? null : index;
    },
    // Affectation directe, sans bascule : au chargement d'un brouillon on restaure la
    // valeur enregistrée telle quelle.
    restoreBookmarkIndex(state, index) {
      state.bookmarkIndex = Number.isInteger(index) ? index : null;
    },
    setRowValidationStatus(state, { index, status }) {
      if (!state.rowStatuses) {
        Vue.set(state, 'rowStatuses', {});
      }
      Vue.set(state.rowStatuses, index, status);
    },
    batchUpdateRows(state, { updates }) {
      updates.forEach(({ index, key, value }) => {
        if (state.jsonResult.results[index]?.[0]?.metadata) {
          Vue.set(state.jsonResult.results[index][0].metadata, key, value);
          // Forcer la réactivité en créant une nouvelle référence pour l'array results[index]
          const updatedResult = { ...state.jsonResult.results[index][0] };
          Vue.set(state.jsonResult.results, index, [updatedResult]);
        }
      });
    },
    updateMetadata(state, { index, key, value }) {
      if (state.jsonResult.results[index]?.[0]?.metadata) {
        Vue.set(state.jsonResult.results[index][0].metadata, key, value)
        
        // Marquer comme manuellement corrigé
        state.jsonResult.results[index][0].isManuallyCorrected = true;
        
        // Forcer la réactivité en créant une nouvelle référence
        const updatedResult = { ...state.jsonResult.results[index][0] };
        Vue.set(state.jsonResult.results, index, [updatedResult]);
      } else {
        console.warn(`❌ [Store] Cannot update metadata - no metadata found at index ${index}`)
      }
    },
    deleteMetadata(state, { index, type, value }) {
      if (state.jsonResult.results[index]?.[0]?.metadata) {
        state.jsonResult.results[index][0].metadata[type] = value !== undefined ? value : null
      }
    },
    setProductType(state, { index, productType }) {
      // S'assurer que jsonResult.results[index] existe
      if (!state.jsonResult.results) {
        state.jsonResult.results = [];
      }
      // S'assurer que l'index existe dans results
      while (state.jsonResult.results.length <= index) {
        state.jsonResult.results.push([]);
      }
      // S'assurer qu'il y a au moins un résultat à l'index
      if (!state.jsonResult.results[index] || state.jsonResult.results[index].length === 0) {
        // Créer un résultat basique avec les données de jsonInput si disponibles
        const excelRow = state.jsonInput[index] || {};
        const basicResult = {
          ean: excelRow.EAN || excelRow.ean || '',
          plu: excelRow.PLU || excelRow.plu || '',
          nom: excelRow.NOM || excelRow.nom || excelRow.Nom || '',
          metadata: {
            productType: productType
          },
          isPlaceholder: true // Marquer comme résultat factice
        };
        state.jsonResult.results[index] = [basicResult];
      } else {
        // Marqué comme corrigé à la main : sans ce drapeau, la catégorisation automatique
        // (rechargement du brouillon, résultats partiels) écrase un choix « Traditionnel ».
        const [chosenResult, ...alternatives] = state.jsonResult.results[index];
        Vue.set(state.jsonResult.results, index, [
          { ...chosenResult, isManuallyCorrected: true, metadata: { ...chosenResult.metadata, productType } },
          ...alternatives
        ]);
      }
    },
    setBatchJobId(state, jobId) {
      state.batchJobId = jobId;
    },
    updateBatchProgress(state, progress) {
      state.batchProgress = {
        processedCount: progress.processedCount || 0,
        totalCount: progress.totalCount || 0,
        status: progress.status || 'pending'
      };
    },
    clearBatchProgress(state) {
      state.batchJobId = null;
      state.batchProgress = {
        processedCount: 0,
        totalCount: 0,
        status: 'pending'
      };
    },
    updatePartialResults(state, { partialResults, replaceAll = false }) {
      if (!state.jsonResult.results) {
        state.jsonResult.results = [];
      }
      
      if (replaceAll) {
        state.jsonResult.results = partialResults || [];
      } else {
        if (Array.isArray(partialResults)) {
          partialResults.forEach((result, resultIndex) => {
            if (result && result.length > 0) {
              Vue.set(state.jsonResult.results, resultIndex, result);
            }
          });
        }
      }
      
      // Apply automatic categorization based on EAN first, then Excel product names
      if (!Array.isArray(state.jsonResult.results) || !Array.isArray(state.jsonInput)) return;

      state.jsonResult.results.forEach((resultSet, resultIndex) => {
        if (!resultSet || resultSet.length === 0) return;
        if (!shouldApplyCategorization(resultSet[0])) return;

        const excelRow = state.jsonInput[resultIndex];
        if (!excelRow) return;

        const excelEAN = getExcelEAN(excelRow, state.columnMapping);
        const excelName = getExcelProductName(excelRow, state.columnMapping);
        const autoType = resolveProductType(state, excelEAN, excelName);

        if (autoType) {
          const updatedResult = {
            ...resultSet[0],
            metadata: {
              ...resultSet[0].metadata,
              productType: autoType
            }
          };
          Vue.set(state.jsonResult.results, resultIndex, [updatedResult, ...resultSet.slice(1)]);
        }
      });
    },
    setAvailableJobs(state, jobs) {
      state.availableJobs = jobs || []
    },
    setSelectedJob(state, job) {
      state.selectedJob = job
    }
  },
  getters: {
    parserResponse(state) {
      if (!Array.isArray(state.jsonInput)) {
        console.warn('[store] jsonInput n\'est pas un array:', state.jsonInput);
        return []
      }
      if (!state.jsonResult || !Array.isArray(state.jsonResult.results)) {
        console.warn('[store] jsonResult.results n\'est pas un array:', state.jsonResult);
        return []
      }
      return state.jsonInput.map((excelRow, index) => {
        const results = state.jsonResult.results[index] || [];
        const chosenResult = results[0] || null;
        const rawProductType = chosenResult?.metadata?.productType || PRODUCT_TYPE_ENUM.TRADITIONNEL;
        const productType = normalizeProductType(rawProductType);
        const isDeleted = state.deletedIndexes.includes(index);
        return {
          excelRow: excelRow,
          parserResults: results,
          chosenResult: chosenResult,
          deleted: isDeleted,
          originalIndex: index,
          productType: productType
        };
      })
    },
    // Une ligne sans EAN, ni PLU, ni nom n'est pas un produit : une feuille Excel déclare souvent des
    // centaines de lignes vides mais mises en forme, et un brouillon peut en avoir gardé. Elles ne
    // doivent ni s'afficher, ni partir en base
    parserResponseFiltered(state, getters) {
      const parserResponse = getters.parserResponse
      if (!Array.isArray(parserResponse)) {
        console.warn('[store] parserResponse n\'est pas un array dans parserResponseFiltered')
        return []
      }
      const isBlank = item => ['EAN', 'PLU', 'NOM']
        .every(field => getExcelValue(item.excelRow, field, state.columnMapping).trim() === '')
      return parserResponse.filter(item => !item.deleted && !isBlank(item))
    },
    jsonInputFiltered(state) {
      if (!Array.isArray(state.jsonInput)) {
        return []
      }
      return state.jsonInput.filter((_, index) => !state.deletedIndexes.includes(index))
    },
    jsonResultFiltered(state) {
      if (!state.jsonResult || !Array.isArray(state.jsonResult.results)) {
        return { originalText: '', results: [] }
      }
      const filteredResults = state.jsonResult.results.filter((_, index) => !state.deletedIndexes.includes(index))
      return {
        ...state.jsonResult,
        results: filteredResults
      }
    },
    batchProgressPercent(state) {
      const { processedCount, totalCount } = state.batchProgress;
      if (totalCount === 0) return 0;
      return Math.round((processedCount / totalCount) * 100);
    },
    isBatchProcessing(state) {
      return state.batchProgress.status === 'processing';
    }
  },
  actions: {
    setJsonInput({ commit }, jsonInput) {
      commit('setJsonInput', jsonInput)
    },
    
    setJsonResult({ commit, state }, jsonResult) {
      if (!jsonResult || !Array.isArray(jsonResult.results)) {
        commit('setJsonResult', jsonResult);
        return;
      }
      
      const categorizedResults = jsonResult.results.map((resultSet, resultIndex) => {
        if (!resultSet || resultSet.length === 0) return resultSet;
        if (!shouldApplyCategorization(resultSet[0])) return resultSet;

        const excelRow = state.jsonInput[resultIndex];
        if (!excelRow) return resultSet;

        const excelEAN = getExcelEAN(excelRow, state.columnMapping);
        const excelName = getExcelProductName(excelRow, state.columnMapping);
        const autoType = resolveProductType(state, excelEAN, excelName);

        if (!autoType) return resultSet;

        const updatedResult = {
          ...resultSet[0],
          metadata: {
            ...resultSet[0].metadata,
            productType: autoType
          }
        };
        return [updatedResult, ...resultSet.slice(1)];
      });
      
      commit('setJsonResult', { ...jsonResult, results: categorizedResults });
    },
    
    setFileProductType({ commit }, productType) {
      commit('setFileProductType', productType)
    },

    setExcelHeaders({ commit }, headers) {
      commit('setExcelHeaders', headers)
    },
    
    setCurrentDraft({ commit }, draftInfo) {
      commit('setCurrentDraft', draftInfo)
    },
    
    clearCurrentDraft({ commit }) {
      commit('clearCurrentDraft')
    },
    
    setParsingMetadata({ commit }, metadata) {
      commit('setParsingMetadata', metadata)
    },
    
    deleteLine({ commit }, index) {
      commit('deleteLine', index)
    },
    
    restoreLine({ commit }, index) {
      commit('restoreLine', index)
    },
    
    resetDeletedIndexes({ commit }) {
      commit('resetDeletedIndexes')
    },
    
    setAsResult({ commit }, { index, resultIndex }) {
      commit('setAsResult', { index, resultIndex })
    },
    setParserPredictions({ commit }, { index, predictions }) {
      commit('setParserPredictions', { index, predictions })
    },
    setBookmarkIndex({ commit }, index) {
      commit('setBookmarkIndex', index)
    },
    setRowValidationStatus({ commit }, { index, status }) {
      commit('setRowValidationStatus', { index, status })
    },
    batchUpdateRows({ commit }, updates) {
      commit('batchUpdateRows', { updates })
    },
    updateMetadata({ commit }, { index, key, value }) {
      commit('updateMetadata', { index, key, value })
    },
    
    deleteMetadata({ commit }, { index, type, value }) {
      commit('deleteMetadata', { index, type, value })
    },
    setProductType({ commit }, { index, productType }) {
      commit('setProductType', { index, productType })
    },
    setBatchJobId({ commit }, jobId) {
      commit('setBatchJobId', jobId)
    },
    updateBatchProgress({ commit }, progress) {
      commit('updateBatchProgress', progress)
    },
    clearBatchProgress({ commit }) {
      commit('clearBatchProgress')
    },
    updatePartialResults({ commit }, { partialResults, replaceAll = false }) {
      commit('updatePartialResults', { partialResults, replaceAll });
    },
    loadJobsForFile({ commit }, idXlsx) {
      return ParserV2Service.getJobsByIdXlsx(idXlsx)
        .then(jobIds => Promise.all(
          jobIds.map(jobId => ParserV2Service.getJobStatus(jobId, { result: false }))
        ))
        .then(jobs => {
          commit('setAvailableJobs', jobs)
          return jobs
        })
    },
    selectAndLoadJob({ commit, dispatch, state }, jobId) {
      // First, get status without results
      return ParserV2Service.getJobStatus(jobId, { result: false })
        .then(job => {
          commit('setSelectedJob', job)
          
          dispatch('updateBatchProgress', {
            processedCount: job.processedCount,
            totalCount: job.totalCount,
            status: job.status
          })
          
          // If completed, fetch results separately
          if (job.status === 'completed') {
            return ParserV2Service.getJobStatus(jobId, { result: true })
              .then(jobWithResults => {
                if (jobWithResults.results && jobWithResults.results.length > 0) {
                  const processedResults = ParserV2Service._processParseResults(jobWithResults.results)
                  
                  // Check if backend returned excelData (new API)
                  if (jobWithResults.excelData && jobWithResults.excelData.rows) {
                    // Use the persisted Excel data from the backend
                    dispatch('setJsonInput', jobWithResults.excelData.rows)
                    
                    // Build column mapping from the persisted mapping
                    if (jobWithResults.excelData.mapping) {
                      const mapping = jobWithResults.excelData.mapping;
                      const columnMapping = Object.keys(mapping).map(field => ({
                        name: field,
                        nameFile: mapping[field]
                      }));
                      commit('setColumnMapping', columnMapping);
                    }
                  } else {
                    // Fallback: Create minimal Excel rows from results if jsonInput is empty
                    // This is for backward compatibility with jobs created before the API change
                    const currentJsonInput = state.jsonInput || []
                    if (!currentJsonInput || currentJsonInput.length === 0) {
                      console.warn('[selectAndLoadJob] No excelData in job response, using fallback reconstruction');
                      const excelRows = processedResults.map((resultArray, index) => {
                        const firstResult = resultArray && resultArray.length > 0 ? resultArray[0] : null
                        // Extract original text from 'text' field which contains "original|matched"
                        const originalText = firstResult?.text?.split('|')[0] || firstResult?.metadata?.textNormalized || `Item ${index + 1}`
                        return {
                          NOM: originalText,
                          EAN: firstResult?.metadata?.ean || '',
                          PLU: firstResult?.metadata?.plu || ''
                        }
                      })
                      dispatch('setJsonInput', excelRows)
                      
                      // Set default column mapping for loaded jobs
                      const defaultMapping = [
                        { name: 'EAN', nameFile: 'EAN' },
                        { name: 'PLU', nameFile: 'PLU' },
                        { name: 'NOM', nameFile: 'NOM' }
                      ]
                      commit('setColumnMapping', defaultMapping)
                    }
                  }
                  
                  dispatch('updatePartialResults', { partialResults: processedResults, replaceAll: true })
                }
                return job
              })
          }
          
          return job
        })
    },

    setParserResponse({ dispatch, state }, parserResponse) {
      if (!Array.isArray(parserResponse)) return

      const jsonInput = parserResponse.map(item => item.excelRow || {})
      const results = parserResponse.map(item => {
        const rawResults = item.parserResults || [];
        const excelEAN = item.excelRow?.EAN || item.excelRow?.ean || '';
        const excelName = item.excelRow?.NOM || item.excelRow?.nom || item.excelRow?.Nom || '';
        const autoType = resolveProductType(state, excelEAN, excelName);

        if (autoType && rawResults.length > 0 && rawResults[0].metadata) {
          // Clone the first result and inject the automatic product type
          const updatedFirstResult = {
            ...rawResults[0],
            metadata: {
              ...rawResults[0].metadata,
              productType: autoType
            }
          };
          return [updatedFirstResult, ...rawResults.slice(1)];
        }
        return rawResults;
      });

      dispatch('setJsonInput', jsonInput)
      dispatch('setJsonResult', { originalText: '', results })
    }
  }
}
